import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Download, Upload, ExternalLink, Tv, FileWarning } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n) => (n === null || n === undefined ? "—" : n);

export default function PunchAttendance() {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (unitId) params.set("unit_id", unitId);
      const { data } = await api.get(`/punch-terminal/attendance-summary?${params}`);
      setSummary(data);
    } catch (e) {
      toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data || []));
  }, []);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [month, unitId]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ month });
      if (unitId) params.set("unit_id", unitId);
      const resp = await api.get(`/punch-terminal/monthly-export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PunchAttendance_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const previewImport = async () => {
    if (!importFile) return toast.error("Select a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(
        `/punch-terminal/monthly-import?month=${month}&confirm_apply=false`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  const applyImport = async () => {
    if (!importFile) return;
    if (!window.confirm(`Apply ${importPreview.changed_count} changes? Existing punches for those dates will be cancelled & replaced.`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(
        `/punch-terminal/monthly-import?month=${month}&confirm_apply=true`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Applied ${data.applied_count} changes`);
      setShowImport(false);
      setImportFile(null);
      setImportPreview(null);
      refresh();
    } catch (e) {
      toast.error("Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="punch-attendance">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Clock className="h-3 w-3" /> Attendance · Punch Terminal
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Punch Attendance Summary
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Aggregated from punch terminals. First punch = IN, last = OUT, others ignored for working hours.</p>
        </div>
        <Button onClick={downloadCsv} variant="outline" size="sm" data-testid="export-csv-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
        <Button onClick={() => setShowImport(true)} variant="outline" size="sm" data-testid="import-csv-btn">
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Import Corrections
        </Button>
      </div>

      {/* Filters + Unit terminal launchers */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-40" data-testid="month-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Unit</Label>
          <Select value={unitId || "all"} onValueChange={(v) => setUnitId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 mt-1" data-testid="unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Units</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 flex justify-end gap-2 flex-wrap">
          {units.map((u) => (
            <Button asChild key={u.id} variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`launch-terminal-${u.id}`}>
              <Link to={`/punch/${u.id}`} target="_blank">
                <Tv className="h-3.5 w-3.5 mr-1" /> Launch {u.name} Terminal
              </Link>
            </Button>
          ))}
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-blue-700">Days in Month</div>
            <div className="text-2xl font-bold text-blue-700" style={{ fontFamily: "Chivo" }}>{summary.days_in_month}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Employees Punched</div>
            <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="stat-employees">{summary.total_employees_with_punches}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-red-700">Total Punches</div>
            <div className="text-2xl font-bold text-red-700" style={{ fontFamily: "Chivo" }} data-testid="stat-punches">{summary.total_punches}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-800">Filter</div>
            <div className="text-sm font-bold text-amber-800">{units.find(u => u.id === unitId)?.name || "All Units"}</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : !summary?.rows?.length ? (
          <div className="p-10 text-center text-slate-400">
            <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No punch data for {month}. Have employees use the terminal kiosk first.</p>
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="summary-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 font-semibold">Punch ID</th>
                <th className="px-3 py-2 font-semibold">Employee</th>
                <th className="px-3 py-2 font-semibold text-right">Days Present</th>
                <th className="px-3 py-2 font-semibold text-right">Total Hours</th>
                <th className="px-3 py-2 font-semibold">Avg/Day</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${r.punch_id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{r.punch_id}</td>
                  <td className="px-3 py-1.5 font-semibold">{r.employee_name}</td>
                  <td className="px-3 py-1.5 text-right">
                    <b>{r.days_present}</b>
                    <span className="text-[10px] text-slate-500 ml-1">/ {summary.days_in_month}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold">{r.total_worked_hhmm}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">
                    {r.days_present > 0
                      ? `${Math.floor(r.total_worked_minutes / r.days_present / 60)}h ${(Math.floor(r.total_worked_minutes / r.days_present) % 60)}m`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import Corrections for {month}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!importPreview ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-start gap-2">
                  <FileWarning className="h-4 w-4 mt-0.5 text-amber-700" />
                  <div>
                    Upload corrected CSV. Required columns: <b>Punch ID, Date (YYYY-MM-DD), First IN (HH:MM), Last OUT (HH:MM)</b>.
                    Existing punches for those dates will be <b>cancelled</b> and new ones recorded. Preview first.
                  </div>
                </div>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0])}
                  className="text-sm"
                  data-testid="csv-file-input"
                />
                {importFile && (
                  <div className="text-xs text-slate-600">Selected: {importFile.name} ({Math.round(importFile.size / 1024)} KB)</div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[10px] text-slate-500">Total Rows</div>
                    <div className="text-xl font-bold">{importPreview.total_rows}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-[10px] text-amber-700">Changed</div>
                    <div className="text-xl font-bold text-amber-700">{importPreview.changed_count}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-[10px] text-emerald-700">Unchanged</div>
                    <div className="text-xl font-bold text-emerald-700">{importPreview.total_rows - importPreview.changed_count}</div>
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-1.5 font-semibold">Punch ID</th>
                        <th className="px-2 py-1.5 font-semibold">Employee</th>
                        <th className="px-2 py-1.5 font-semibold">Date</th>
                        <th className="px-2 py-1.5 font-semibold">Old IN → New IN</th>
                        <th className="px-2 py-1.5 font-semibold">Old OUT → New OUT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.diff.filter((d) => d.changed).map((d, i) => (
                        <tr key={i} className="border-t border-slate-100 bg-amber-50/40" data-testid={`diff-row-${i}`}>
                          <td className="px-2 py-1 font-mono text-[10px]">{d.punch_id}</td>
                          <td className="px-2 py-1">{d.employee_name || d.error}</td>
                          <td className="px-2 py-1">{d.date}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            <span className="text-slate-400 line-through">{d.old_in}</span>{" "}
                            <b className="text-emerald-700">{d.new_in}</b>
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">
                            <span className="text-slate-400 line-through">{d.old_out}</span>{" "}
                            <b className="text-emerald-700">{d.new_out}</b>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImport(false); setImportFile(null); setImportPreview(null); }}>Cancel</Button>
            {!importPreview ? (
              <Button onClick={previewImport} disabled={!importFile || busy} className="brand-bg brand-bg-hover text-white" data-testid="preview-import-btn">
                {busy ? "Previewing…" : "Preview Changes"}
              </Button>
            ) : (
              <>
                <Button onClick={() => setImportPreview(null)} variant="outline">Back</Button>
                <Button onClick={applyImport} disabled={busy || importPreview.changed_count === 0} className="brand-bg brand-bg-hover text-white" data-testid="apply-import-btn">
                  {busy ? "Applying…" : `Apply ${importPreview.changed_count} Changes`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
